import { CaretDownIcon } from '@phosphor-icons/react';
import type { ReactNode } from 'react';
import {
  Button as AriaButton,
  Disclosure as AriaDisclosure,
  DisclosurePanel as AriaDisclosurePanel,
  Heading as AriaHeading,
} from 'react-aria-components';
import { cn } from './lib/cn';

export type CardProps = {
  children: ReactNode;
  /** Omit to get a card with no header at all. */
  title?: ReactNode;
  /** A 14px icon before the title, as the prototype's section cards (Business Details, Connection). */
  icon?: ReactNode;
  /** Sits opposite the title: a status badge, an edit control. */
  action?: ReactNode;
  className?: string;
  bodyClassName?: string;
  /**
   * The title folds the body away and back. Needs a title. Leave
   * `isExpanded` unset to let the card manage itself.
   */
  collapsible?: boolean;
  defaultExpanded?: boolean;
  isExpanded?: boolean;
  onExpandedChange?: (isExpanded: boolean) => void;
};

/* The hairline is the edge; the shadow alone melts into a white page. */
const SURFACE =
  'sk:flex sk:flex-col sk:rounded-card sk:border-hairline sk:border-border-subtle sk:bg-surface sk:shadow-card';
const HEADER =
  'sk:flex sk:items-center sk:justify-between sk:gap-2 sk:px-4 sk:py-3';
const TITLE = 'sk:m-0 sk:text-sm sk:font-semibold sk:text-fg';

function CollapsibleCard({
  children,
  title,
  action,
  className,
  bodyClassName,
  defaultExpanded,
  isExpanded,
  onExpandedChange,
}: CardProps) {
  return (
    <AriaDisclosure
      defaultExpanded={defaultExpanded}
      isExpanded={isExpanded}
      onExpandedChange={onExpandedChange}
      className={cn('sk:group', SURFACE, className)}
    >
      {/* The rule shows only while there is a body under it. */}
      <header
        className={cn(HEADER, 'sk:group-rac-expanded:border-b sk:border-rule')}
      >
        <AriaHeading level={2} className={cn(TITLE, 'sk:min-w-0 sk:flex-1')}>
          <AriaButton
            slot="trigger"
            className={cn(
              'sk:flex sk:w-full sk:cursor-pointer sk:items-center sk:gap-2',
              'sk:m-0 sk:appearance-none sk:border-0 sk:bg-transparent sk:p-0 sk:text-left',
              'sk:font-sans sk:text-sm sk:font-semibold sk:text-fg',
              'sk:outline-none sk:rac-focus-visible:outline-2 sk:rac-focus-visible:outline-solid sk:rac-focus-visible:outline-offset-2 sk:rac-focus-visible:outline-primary',
            )}
          >
            <CaretDownIcon
              size={14}
              weight="bold"
              aria-hidden
              className={cn(
                'sk:shrink-0 sk:text-fg-tertiary',
                'sk:-rotate-90 sk:transition-transform sk:duration-150',
                'sk:group-rac-expanded:rotate-0',
              )}
            />
            {title}
          </AriaButton>
        </AriaHeading>
        {action}
      </header>
      <AriaDisclosurePanel>
        <div className={cn('sk:p-4', bodyClassName)}>{children}</div>
      </AriaDisclosurePanel>
    </AriaDisclosure>
  );
}

/** The page's unit of grouping: one surface, one heading, one subject. */
export function Card(props: CardProps) {
  const {
    children,
    title,
    icon,
    action,
    className,
    bodyClassName,
    collapsible,
  } = props;

  if (collapsible && title) {
    return <CollapsibleCard {...props} />;
  }

  return (
    <section className={cn(SURFACE, className)}>
      {title ? (
        <header className={cn(HEADER, 'sk:border-b sk:border-rule')}>
          <h2 className={cn(TITLE, icon && 'sk:flex sk:items-center sk:gap-2')}>
            {icon ? (
              <span aria-hidden className="sk:flex sk:text-fg-tertiary">
                {icon}
              </span>
            ) : null}
            {title}
          </h2>
          {action}
        </header>
      ) : null}
      <div className={cn('sk:p-4', bodyClassName)}>{children}</div>
    </section>
  );
}

export type FieldProps = {
  label: ReactNode;
  children: ReactNode;
};

/**
 * One label above its value. Stacked rather than side by side so that long
 * values (addresses, provider names) wrap without shrinking the label.
 */
export function Field({ label, children }: FieldProps) {
  return (
    <div className="sk:flex sk:min-w-0 sk:flex-col sk:gap-0.5">
      <span className="sk:text-xs sk:text-fg-tertiary">{label}</span>
      <span className="sk:min-w-0 sk:text-dense sk:font-medium sk:text-fg sk:tracking-dense">
        {children}
      </span>
    </div>
  );
}

export type FieldGridProps = {
  children: ReactNode;
  /** A fixed count, for a form whose fields line up with the card beside it. */
  columns?: 2 | 3 | 4;
  className?: string;
};

const COLUMNS = {
  2: 'sk:grid-cols-2',
  3: 'sk:grid-cols-3',
  4: 'sk:grid-cols-4',
};

/**
 * Fields in even columns; without a `columns` count they reflow instead of
 * squeezing on a narrow card. Form fields use it too, so a form and its
 * summary keep the same rhythm.
 */
export function FieldGrid({ children, columns, className }: FieldGridProps) {
  return (
    <div
      className={cn(
        'sk:grid sk:gap-x-4 sk:gap-y-3',
        columns
          ? COLUMNS[columns]
          : 'sk:grid-cols-[repeat(auto-fit,minmax(140px,1fr))]',
        className,
      )}
    >
      {children}
    </div>
  );
}
